import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { canWrite, canDelete, WRITE_PERMISSION_DENIED, DELETE_PERMISSION_DENIED } from '@/services/permissions';
import { recordAudit } from '@/services/audit';

// Línea objetivo de una promoción. Además del alcance (global o referencias),
// se puede limitar a la línea premium, a las sencillas, o a ambas.
export type DiscountLineType = 'both' | 'premium' | 'sencilla';

export const DISCOUNT_LINE_OPTIONS: Array<{ value: DiscountLineType; label: string }> = [
  { value: 'both', label: 'Ambas líneas' },
  { value: 'premium', label: 'Solo premium' },
  { value: 'sencilla', label: 'Solo sencillas' },
];

export function discountLineLabel(lineType: string): string {
  const found = DISCOUNT_LINE_OPTIONS.find((o) => o.value === lineType);
  return found ? found.label : 'Ambas líneas';
}

// Promoción por rango de fechas y referencias específicas (tabla `discounts`).
export interface Discount {
  id: string;
  name: string;
  discountPercent: number;
  startDate: string;
  endDate: string;
  isGlobal: boolean;
  productIds: string[];
  // Línea a la que aplica: 'both' | 'premium' | 'sencilla'.
  lineType: string;
  isActive: boolean;
  createdAt: string;
}

function mapRow(row: Record<string, any>): Discount {
  return {
    id: row.id,
    name: row.name ?? '',
    discountPercent: Number(row.discount_percent ?? 0),
    startDate: row.start_date ?? '',
    endDate: row.end_date ?? '',
    isGlobal: row.is_global ?? false,
    productIds: Array.isArray(row.product_ids) ? row.product_ids : [],
    lineType: row.line_type ?? 'both',
    isActive: row.is_active ?? true,
    createdAt: row.created_at ?? '',
  };
}

// Determina si una promoción está vigente AHORA (activa y dentro del rango).
// Una fecha vacía se interpreta como "sin límite" por ese lado.
export function isDiscountLive(d: Discount, now: Date = new Date()): boolean {
  if (!d.isActive) return false;
  if (d.startDate && new Date(d.startDate).getTime() > now.getTime()) return false;
  if (d.endDate && new Date(d.endDate).getTime() < now.getTime()) return false;
  return d.discountPercent > 0;
}

// Porcentaje aplicable a un producto: el mayor descuento vigente que aplique
// (global o que incluya la referencia) Y cuya línea objetivo coincida con la
// línea del producto. Devuelve 0 si no hay promoción.
export function getProductDiscountPercent(
  productId: string,
  discounts: Discount[],
  lineType?: string,
  now: Date = new Date(),
): number {
  let best = 0;
  const target = lineType && lineType.length > 0 ? lineType : 'both';
  for (const d of discounts) {
    if (!isDiscountLive(d, now)) continue;
    // Filtro de línea: 'both' aplica a todo; si no, debe coincidir con la línea.
    if (d.lineType && d.lineType !== 'both' && d.lineType !== target) continue;
    if (d.isGlobal || d.productIds.includes(productId)) {
      if (d.discountPercent > best) best = d.discountPercent;
    }
  }
  return best;
}

// Precio final con descuento aplicado.
export function discountedPrice(price: number, percent: number): number {
  if (percent <= 0) return price;
  return Math.round(price * (1 - percent / 100));
}

// Promociones vigentes para la tienda pública.
export async function fetchActiveDiscounts(): Promise<Discount[]> {
  if (!isSupabaseConfigured || !supabase) return [];
  try {
    const { data, error } = await supabase.from('discounts').select('*').eq('is_active', true);
    if (error) throw new Error(error.message);
    return ((data ?? []) as Array<Record<string, any>>).map(mapRow).filter((d) => isDiscountLive(d));
  } catch (err) {
    console.warn('[discounts] active fetch failed:', err);
    return [];
  }
}

// Todas las promociones (para el panel de administración).
export async function fetchDiscounts(): Promise<Discount[]> {
  if (!isSupabaseConfigured || !supabase) return [];
  const { data, error } = await supabase
    .from('discounts')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return ((data ?? []) as Array<Record<string, any>>).map(mapRow);
}

export interface DiscountInput {
  id?: string;
  name: string;
  discountPercent: number;
  startDate: string;
  endDate: string;
  isGlobal: boolean;
  productIds: string[];
  lineType: string;
  isActive: boolean;
}

export async function saveDiscount(input: DiscountInput): Promise<{ error: string | null }> {
  if (!(await canWrite('discounts'))) return { error: WRITE_PERMISSION_DENIED };
  if (!isSupabaseConfigured || !supabase) return { error: 'Supabase no está configurado.' };
  if (!input.name.trim()) return { error: 'El nombre de la promoción es obligatorio.' };
  if (!input.discountPercent || input.discountPercent <= 0 || input.discountPercent > 100) {
    return { error: 'El porcentaje de descuento debe estar entre 1 y 100.' };
  }
  if (!input.isGlobal && input.productIds.length === 0) {
    return { error: 'Selecciona al menos una referencia o marca la promoción como Global.' };
  }
  if (input.startDate && input.endDate && new Date(input.endDate) < new Date(input.startDate)) {
    return { error: 'La fecha de fin no puede ser anterior a la de inicio.' };
  }

  const payload = {
    name: input.name.trim(),
    discount_percent: input.discountPercent,
    start_date: input.startDate ? new Date(input.startDate).toISOString() : null,
    end_date: input.endDate ? new Date(input.endDate).toISOString() : null,
    is_global: input.isGlobal,
    product_ids: input.isGlobal ? [] : input.productIds,
    line_type: input.lineType || 'both',
    is_active: input.isActive,
  };

  try {
    if (input.id) {
      const { error } = await supabase.from('discounts').update(payload).eq('id', input.id);
      if (error) return { error: error.message };
      recordAudit({ entityType: 'settings', entityId: input.id, action: 'UPDATE', newValue: payload.name });
    } else {
      const { data, error } = await supabase.from('discounts').insert(payload).select('id');
      if (error) return { error: error.message };
      recordAudit({
        entityType: 'settings',
        entityId: (data?.[0] as { id: string } | undefined)?.id ?? '',
        action: 'CREATE',
        newValue: payload.name,
      });
    }
    return { error: null };
  } catch (err) {
    console.warn('[discounts] save failed:', err);
    return { error: 'Error de conexión al guardar la promoción.' };
  }
}

export async function toggleDiscount(id: string, isActive: boolean): Promise<{ error: string | null }> {
  if (!(await canWrite('discounts'))) return { error: WRITE_PERMISSION_DENIED };
  if (!isSupabaseConfigured || !supabase) return { error: 'Supabase no está configurado.' };
  const { error } = await supabase.from('discounts').update({ is_active: isActive }).eq('id', id);
  return { error: error ? error.message : null };
}

export async function deleteDiscount(id: string): Promise<{ error: string | null }> {
  if (!(await canDelete('discounts'))) return { error: DELETE_PERMISSION_DENIED };
  if (!isSupabaseConfigured || !supabase) return { error: 'Supabase no está configurado.' };
  try {
    const { error } = await supabase.from('discounts').delete().eq('id', id);
    if (error) return { error: error.message };
    recordAudit({ entityType: 'settings', entityId: id, action: 'DELETE' });
    return { error: null };
  } catch (err) {
    console.warn('[discounts] delete failed:', err);
    return { error: 'Error de conexión al eliminar la promoción.' };
  }
}